import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Row, Col, Form, Button, Spinner, Alert, Card } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";

export default function ServiceContactPage() {
    const [formData, setFormData] = useState({
        phone: "",
        email: "",
        whatsapp: "",
        imo: ""
    });

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [error, setError] = useState(null);
    const [successMsg, setSuccessMsg] = useState(null);

    // Fetch existing contact info
    const fetchServiceContact = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/web-manager/service-contact`,
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data?.success && response.data?.data) {
                setFormData({
                    phone: response.data.data.phone || "",
                    email: response.data.data.email || "",
                    whatsapp: response.data.data.whatsapp || "",
                    imo: response.data.data.imo || ""
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || "Failed to load service contact info.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServiceContact();
    }, []);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    // Save or Update data
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError(null);
        setSuccessMsg(null);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/web-manager/service-contact`,
                formData,
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data?.success) {
                setSuccessMsg(response.data.message || "Service contact saved successfully.");
                setIsEditing(false);
            }
        } catch (err) {
            setError(err.response?.data?.message || "Something went wrong while saving.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageLayout>
            <Row className="g-4">
                {/* Header */}
                <Col xl={12}>
                    <div className="bg-white p-4 rounded-3 shadow-sm border border-light-subtle d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            <h3 className="fw-bold text-dark m-0 fs-4">Service Contact</h3>
                        </div>
                        <ul className="list-unstyled d-flex align-items-center gap-2 m-0 small text-secondary">
                            <li><Link to="#" className="text-decoration-none text-primary">Home</Link></li>
                            <li className="text-muted">~</li>
                            <li><Link to="#" className="text-decoration-none text-primary">Web Manager</Link></li>
                            <li className="text-muted">~</li>
                            <li className="fw-semibold text-secondary">Service Contact</li>
                        </ul>
                    </div>
                </Col>

                {/* Main Content */}
                <Col xl={8} className="mx-auto">
                    <Card className="bg-white rounded-3 shadow-sm border border-light-subtle overflow-hidden">
                        <Card.Header className="bg-white border-bottom p-4 d-flex justify-content-between align-items-center">
                            <h5 className="fw-bold text-dark m-0">Contact Details</h5>
                            {!isEditing && !loading && (
                                <Button
                                    variant="primary"
                                    size="sm"
                                    className="px-4 py-1.5 fw-bold rounded-2 shadow-sm"
                                    onClick={() => setIsEditing(true)}
                                >
                                    <i className="bi bi-pencil-square me-1"></i> Edit Details
                                </Button>
                            )}
                        </Card.Header>

                        <Card.Body className="p-4">
                            {error && <Alert variant="danger" className="mb-4 rounded-2">{error}</Alert>}
                            {successMsg && <Alert variant="success" className="mb-4 rounded-2">{successMsg}</Alert>}

                            {loading ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" variant="primary" />
                                    <p className="mt-2 text-muted fw-semibold">Loading details...</p>
                                </div>
                            ) : !isEditing ? (
                                /* VIEW MODE */
                                <Row className="g-4">
                                    <Col md={6}>
                                        <div className="p-3 rounded-3 bg-light border border-light-subtle">
                                            <span className="text-uppercase fw-bold text-muted fs-7 d-block mb-1">Phone Number</span>
                                            <span className="fw-semibold text-dark fs-6">{formData.phone || "N/A"}</span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="p-3 rounded-3 bg-light border border-light-subtle">
                                            <span className="text-uppercase fw-bold text-muted fs-7 d-block mb-1">Email Address</span>
                                            <span className="fw-semibold text-dark fs-6">{formData.email || "N/A"}</span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="p-3 rounded-3 bg-light border border-light-subtle">
                                            <span className="text-uppercase fw-bold text-muted fs-7 d-block mb-1">WhatsApp</span>
                                            <span className="fw-semibold text-dark fs-6">{formData.whatsapp || "N/A"}</span>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="p-3 rounded-3 bg-light border border-light-subtle">
                                            <span className="text-uppercase fw-bold text-muted fs-7 d-block mb-1">IMO</span>
                                            <span className="fw-semibold text-dark fs-6">{formData.imo || "N/A"}</span>
                                        </div>
                                    </Col>
                                </Row>
                            ) : (
                                /* EDIT MODE */
                                <Form onSubmit={handleSubmit}>
                                    <Row className="g-3">
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-bold text-secondary small text-uppercase">Phone</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="phone"
                                                    placeholder="Enter phone number"
                                                    value={formData.phone}
                                                    onChange={handleChange}
                                                    className="shadow-none border-0 bg-light p-2.5 rounded-3 fs-6"
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-bold text-secondary small text-uppercase">Email</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    placeholder="Enter email address"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    className="shadow-none border-0 bg-light p-2.5 rounded-3 fs-6"
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-bold text-secondary small text-uppercase">WhatsApp</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="whatsapp"
                                                    placeholder="Enter WhatsApp number"
                                                    value={formData.whatsapp}
                                                    onChange={handleChange}
                                                    className="shadow-none border-0 bg-light p-2.5 rounded-3 fs-6"
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="fw-bold text-secondary small text-uppercase">IMO</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="imo"
                                                    placeholder="Enter IMO number"
                                                    value={formData.imo}
                                                    onChange={handleChange}
                                                    className="shadow-none border-0 bg-light p-2.5 rounded-3 fs-6"
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={12} className="mt-4 d-flex justify-content-end gap-2">
                                            <Button
                                                variant="light"
                                                type="button"
                                                className="px-4 py-2 fw-bold text-secondary rounded-2 border-0"
                                                onClick={() => setIsEditing(false)}
                                                disabled={submitting}
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                variant="primary"
                                                type="submit"
                                                className="px-4 py-2 fw-bold text-uppercase rounded-2 shadow-sm"
                                                disabled={submitting}
                                            >
                                                {submitting ? <Spinner animation="border" size="sm" /> : "Save Changes"}
                                            </Button>
                                        </Col>
                                    </Row>
                                </Form>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}